import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLDivElement} [paginationButtons] - The pagination buttons container.
 * @property {HTMLButtonElement} [paginationNext] - The next page button.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #filterUpdateInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Pre-fetch pages using current URL (which includes sort_by and filters)
    // This ensures pages are fetched with the correct sort order from the start
    this.#fetchPage('next');
    this.#fetchPage('previous');

    // Check pagination type from data attribute
    const paginationType = this.getAttribute('data-pagination-type') || 'infinite';

    if (paginationType === 'button') {
      this.#setupButtonPagination();
    } else {
      this.#observeViewMore();
      // Also update visibility for infinite scroll
      this.#updateButtonVisibility();
      requestIdleCallback(() => {
        this.#updateButtonVisibility();
      });
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    
    // Final check after everything is set up
    setTimeout(() => {
      this.#updateButtonVisibility();
    }, 100);
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   * Updates button visibility after refs are refreshed.
   */
  updatedCallback() {
    super.updatedCallback();
    
    // Sort by price_max if price sorting is selected
    this.#sortByPriceMax();
    
    // Update button visibility after component is morphed and refs are updated
    // This ensures the button shows/hides correctly after sorting/filtering
    requestIdleCallback(() => {
      this.#updateButtonVisibility();
    });
    
    // Also update after a short delay to ensure DOM is fully settled
    setTimeout(() => {
      this.#updateButtonVisibility();
    }, 100);
  }

  /**
   * Sorts products by price_max (most expensive variant) when price sorting is selected
   */
  #sortByPriceMax() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    
    // Only sort if price sorting is selected
    if (sortBy !== 'price-ascending' && sortBy !== 'price-descending') {
      return;
    }

    const { grid, cards } = this.refs;
    if (!grid || !Array.isArray(cards) || cards.length === 0) {
      return;
    }

    // Get all product cards with their price_max values
    const productCards = Array.from(cards).map((card) => {
      const priceMax = parseInt(card.getAttribute('data-price-max')) || 0;
      return { card, priceMax };
    });

    // Sort by price_max
    const isAscending = sortBy === 'price-ascending';
    productCards.sort((a, b) => {
      if (isAscending) {
        return a.priceMax - b.priceMax; // Low to high
      } else {
        return b.priceMax - a.priceMax; // High to low
      }
    });

    // Reorder the cards in the DOM
    productCards.forEach(({ card }) => {
      grid.appendChild(card);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #setupButtonPagination() {
    const { paginationNext } = this.refs;

    if (paginationNext) {
      paginationNext.addEventListener('click', async () => {
        await this.#renderNextPage();
      });
    }

    // Set initial button visibility - call immediately and also in idle callback
    this.#updateButtonVisibility();
    requestIdleCallback(() => {
      this.#updateButtonVisibility();
    });
  }

  #updateButtonVisibility() {
    const { paginationNext, viewMoreNext, grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const { cards } = this.refs;

    // If no cards, hide everything
    if (!Array.isArray(cards) || cards.length === 0) {
      if (paginationNext) paginationNext.style.display = 'none';
      if (viewMoreNext) viewMoreNext.style.display = 'none';
      return;
    }

    // Get the page number of the last card
    const lastCard = cards[cards.length - 1];
    let lastCardPage = lastCard ? Number(lastCard.dataset.page) || 1 : 1;
    
    // Fallback: check URL parameter if data-page isn't available
    if (!lastCardPage || lastCardPage === 1) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlPage = urlParams.get('page');
      if (urlPage) {
        lastCardPage = Number(urlPage);
      }
    }
    
    // Check if we're on the last page
    // If lastCardPage equals or exceeds lastPage, we're on or past the last page (no more products)
    const hasMorePages = lastCardPage < lastPage && lastPage > 1;

    // Show/hide next button (for button pagination)
    if (paginationNext) {
      if (hasMorePages) {
        paginationNext.style.display = 'inline-flex';
        paginationNext.disabled = false;
      } else {
        paginationNext.style.display = 'none';
        paginationNext.disabled = true;
      }
    }

    // Show/hide viewMoreNext span (for infinite scroll)
    if (viewMoreNext) {
      viewMoreNext.style.display = hasMorePages ? 'block' : 'none';
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      // Create URL from current location to preserve all query parameters (including sort_by, filters, etc.)
      // Always use the current window.location to ensure we have the latest sort/filter parameters
      const newUrl = new URL(window.location.href);
      
      // Ensure we preserve all existing query parameters (sort_by, filters, etc.)
      // Only update the page parameter - all other params (including sort_by) are preserved automatically
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    
    // Fetch the page with the URL that includes all current query parameters
    // The buildSectionRenderingURL function will preserve all query params including sort_by
    // Use cache=false when fetching after a filter update to ensure fresh content with correct sort
    const useCache = !this.#filterUpdateInProgress;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, useCache, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Update URL in browser history, preserving all query parameters including sort_by
    history.pushState('', '', nextPage.url.toString());

    // Update button visibility after adding new products
    this.#updateButtonVisibility();
    
    // Re-sort by price_max if price sorting is active (in case new products need sorting)
    this.#sortByPriceMax();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    // Update button visibility after adding previous page products
    this.#updateButtonVisibility();

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    // Create URL from current location to preserve all query parameters (including sort_by, filters, etc.)
    // This ensures that when loading more pages, the sort order and filters are maintained
    const url = new URL(window.location.href);
    
    // Preserve all existing query parameters and only update the page parameter
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    // Mark that a filter update is in progress
    this.#filterUpdateInProgress = true;
    
    // Clear all cached pages when filters/sort change
    // This ensures subsequent pages are fetched with the new sort order
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;
    let hasSeenCardUpdate = false;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;
      
      // Also check if cards have been updated (childList changes)
      const { cards } = this.refs;
      const hasCards = Array.isArray(cards) && cards.length > 0;
      
      // If we see cards and the lastPage has changed, or if we see cards for the first time
      if (hasCards && !hasSeenCardUpdate) {
        hasSeenCardUpdate = true;
      }

      // Update when lastPage changes OR when we have cards and lastPage is valid
      if (newLastPage !== currentLastPage || (hasCards && newLastPage && hasSeenCardUpdate)) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        const paginationType = this.getAttribute('data-pagination-type') || 'infinite';
        if (paginationType === 'button') {
          this.#setupButtonPagination();
        } else {
          this.#observeViewMore();
        }

        // Explicitly update button visibility after a short delay to ensure refs are updated
        setTimeout(() => {
          this.#updateButtonVisibility();
          // Also sort by price_max if price sorting is selected
          this.#sortByPriceMax();
          // Reset filter update flag after a delay to allow fresh fetches
          setTimeout(() => {
            this.#filterUpdateInProgress = false;
          }, 500);
        }, 100);

        // Fetch the next page (will use cache=false due to #filterUpdateInProgress flag)
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
        subtree: true, // Watch subtree to catch nested changes
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
        // Final update after timeout to ensure button visibility is correct
        if (this.isConnected) {
          this.#updateButtonVisibility();
          // Also sort by price_max if price sorting is selected
          this.#sortByPriceMax();
          // Reset filter update flag
          this.#filterUpdateInProgress = false;
        }
      }, 3000);
    }
  };
}
